import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import isHotkey from 'is-hotkey'
import {
  Editable,
  withReact,
  useSlate,
  Slate,
  useSlateStatic,
  useSelected,
  useFocused,
  ReactEditor,
} from 'slate-react'
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
  Text,
  Point,
  Range,
  Path,
} from 'slate'
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { withHistory } from 'slate-history'
import { Button, Icon, Toolbar } from './RichTextComp'
import BackupTableIcon from '@mui/icons-material/BackupTable';
import isUrl from 'is-url'
import imageExtensions from 'image-extensions'
import { css } from '@emotion/css'
import escapeHtml from 'escape-html'
import { Typography, Grid2, IconButton, Modal, Box, Menu, MenuItem, Tooltip, CircularProgress } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
//import RichTextAddEntryPopup from './RichTextAddEntryPopup'
import AddExistingEntryPopup from '../AddExistingEntryPopup';
import ExistingMediaUploadPopup from '../Media/ExistingMediaUploadPopup';
import AddMedia from '../../pages/media/AddMedia';
import { getAssets } from '../../actions/mediaAction';
import { useSelector, useDispatch } from 'react-redux';
import { getEntryDetails } from '../../api/api';
import DeleteIcon from '../../assets/images/icons/delete_icon.svg'
import ModalBox from '../../themes/overrides/ModalBox'
import TextFieldInput from '../../themes/overrides/TextFieldInput'
import AddButton from '../../themes/overrides/AddButton'
import EditIcon from '../icons/EditIcon'
import PropTypes from 'prop-types';
import OutlinedAddButton from '../../themes/overrides/OutlinedAddButton';
import ColorPickerButton from './tools/ColorPickerButton';
import NumberedListButton from './tools/NumberedListButton';
import LinkButton from './tools/LinkButton';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
  'mod+a': 'code',
  'mod+h': 'highlight',
  'mod+shift+x': 'strikethrough',
}
const LIST_TYPES = ['numbered-list', 'bulleted-list']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

const RichText = ({ onChange, value, locale, disabled, id }) => {
  const [entryDetails, setEntryDetails] = useState({});
  const renderElement = useCallback(props => <Element {...props} entryDetails={entryDetails} />, [entryDetails])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const editor = useMemo(() => withHistory(withReact(createEditor())), [])
  const editor = useMemo(
    () => withLinks(withTables(withImages(withHistory(withReact(createEditor()))))),
    []
  );
  const workspaceId = localStorage.getItem('workspaceID');

  // Editor reset effect
  useEffect(() => {
    if (editor && value) {
      // Only update if content changed
      if (JSON.stringify(editor.children) !== JSON.stringify(value)) {
        // Save the current selection path
        const hadSelection = editor.selection;

        // Apply the new value
        editor.children = value;

        // Reset selection safely - either to saved position or start
        try {
          if (hadSelection &&
            hadSelection.anchor.path[0] < value.length &&
            hadSelection.focus.path[0] < value.length) {
            // Try to restore original selection if paths still valid
            Transforms.select(editor, hadSelection);
          } else {
            // Fallback to start of document
            Transforms.select(editor, {
              anchor: { path: [0, 0], offset: 0 },
              focus: { path: [0, 0], offset: 0 }
            });
          }
        } catch (error) {
          console.warn('Selection reset error:', error);
          Transforms.deselect(editor);
        }

        // Force editor update
        editor.onChange();
      }
    }
  }, [value, editor]);

  // Function to fetch entry details, updated details (used for getting embed entry status)
  const fetchEntryDetails = useCallback(async (entryIds) => {
    if (entryIds.length === 0) return;

    try {
      const response = await getEntryDetails(workspaceId, entryIds);
      if (response.data && Array.isArray(response.data.entry)) {
        const detailsMap = {};
        response.data.entry.forEach(entry => {
          detailsMap[entry._id] = {
            published: entry.published,
            identifier: entry.identifier,
            content_type_name: entry.contentTypeId
          };
        });
        setEntryDetails(detailsMap);
      } else {
        console.warn('Unexpected API response structure:', response);
      }
    } catch (error) {
      console.error('Error fetching entry details:', error);
    }
  }, [workspaceId]);

  // Effect to fetch entry details when content changes
  useEffect(() => {
    if (editor) {
      const entryIds = getAllEntryIdsFromEditor(editor);
      if (entryIds.length > 0) {
        fetchEntryDetails(entryIds);
      }
    }
  }, [editor, value, fetchEntryDetails]);


  const handleChange = () => {
    if (onChange) {
      onChange(editor.children); // Pass the serialized value to the parent
    }
  };

  const initialValue = value ? value : [
    {
      type: 'paragraph',
      children: [
        { text: '' }
      ],
    },

  ];


  return (
    <Box sx={{
      height: 520,
      borderRadius: 2,
      borderColor: "#ccc",
      borderWidth: '1px',
      overflowY: {
        xs: 'unset',
        md: 'auto'
      },
      fontSize: '1rem',
    }}
    >
      <Slate key={`${id}-${locale}`} editor={editor} initialValue={initialValue} onChange={handleChange} >
        {!disabled && (
          <Toolbar style={{ padding: '14px 20px 8px 15px', display: { xs: 'block', md: 'flex' }, justifyContent: { md: 'space-between' }, borderRadius: '10px 10px 0px 0px', position: 'relative' }}>
            <MarkButton format="bold" icon="format_bold" tooltip="Bold (Ctrl+B)" />
            <MarkButton format="italic" icon="format_italic" tooltip="Italic (Ctrl+I)" />
            <MarkButton format="underline" icon="format_underlined" tooltip="Underline (Ctrl+U)" />
            <MarkButton format="code" icon="code" tooltip="Code (Ctrl+`)" />
            <MarkButton format="highlight" icon="highlight" tooltip="Highlight (Ctrl+H)" />
            <MarkButton format="strikethrough" icon="strikethrough_s" tooltip="Strikethrough (Ctrl+Shift+X)" />
            <ColorPickerButton />
            <BlockButton format="heading-one" icon="looks_one" tooltip="Heading 1" />
            <BlockButton format="heading-two" icon="looks_two" tooltip="Heading 2" />
            <BlockButton format="heading-three" icon="looks_3" tooltip="Heading 3" />
            <BlockButton format="heading-four" icon="looks_4" tooltip="Heading 4" />
            {/* <BlockButton format="block-quote" icon="format_quote" tooltip="Blockquote" /> */}
            <NumberedListButton />
            <BulletListButton />
            <BlockButton format="left" icon="format_align_left" tooltip="Align Left" />
            <BlockButton format="center" icon="format_align_center" tooltip="Align Center" />
            <BlockButton format="right" icon="format_align_right" tooltip="Align Right" />
            <BlockButton format="justify" icon="format_align_justify" tooltip="Justify" />
            <BlockButton format="hr" icon="horizontal_rule" tooltip="Horizontal Rule" />
            {/* <InsertImageButton />
            <InsertURLImageButton /> */}
            <LinkButton />
            <InsertTableButton locale={locale} editorId={id} />
            <EmbedButton />
          </Toolbar>
        )}
        <Editable
          // renderElement={props => <Element {...props} />} // Render elements
          renderElement={(props) => renderElement({ ...props, locale, id })}
          renderLeaf={renderLeaf} // Render leaves
          placeholder="..."
          spellCheck
          readOnly={disabled}
          style={{
            height: '450px', overflowY: 'auto', outline: 'none', marginLeft: '15px', marginRight: '15px', marginTop: '15px',
          }}
          // autoFocus
          onKeyDown={event => {

            // Handle mod+a hotkey
            if (isHotkey('mod+a', event)) {
              event.preventDefault();
              Transforms.select(editor, []);
              return;
            }

            // Handle dynamic HOTKEYS
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
              }
            }

            // Handle Enter key behavior (Insert a new list item or a new paragraph)
            if (event.shiftKey && event.key === "Enter") {
              event.preventDefault();
              Transforms.insertText(editor, "\n"); // Insert line break
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const isListItem = isBlockActive(editor, 'bulleted-list', 'type') || isBlockActive(editor, 'numbered-list', 'type');

              if (isListItem) {
                // Insert a new list item if a list is active
                const newNode = {
                  type: 'list-item',
                  children: [{ text: '' }],
                };
                Transforms.insertNodes(editor, newNode);
              } else {
                // Insert a new paragraph if no list is active
                const newParagraph = {
                  type: 'paragraph',
                  children: [{ text: '' }],
                };

                Transforms.insertNodes(editor, newParagraph);
              }
            }
          }}
        />
      </Slate>
    </Box>
  )
}
const toggleBlock = (editor, format) => {
  if (format === 'hr') {
    Transforms.insertNodes(editor, {
      type: 'hr',
      children: [{ text: '' }],
    });

    // Insert a new paragraph after the hr element
    Transforms.insertNodes(editor, {
      type: 'paragraph',
      children: [{ text: '' }],
    });

    // Move the cursor to the new paragraph
    Transforms.move(editor, { distance: 1 });
    return;
  }

  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);

  // Unwrap existing lists if toggling off
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties;
  // If format is related to text alignment, apply the alignment to the table cells
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
    // Apply text alignment to selected table cells
    Transforms.setNodes(editor, newProperties, {
      match: (n) => n.type === 'table-cell',
    });
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }

  // Apply changes to nodes
  Transforms.setNodes(editor, newProperties);

  // Wrap selection inside list if turning it on (only if the list format is active)
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);

    // Ensure all items are list-items (wrap any paragraphs as list items)
    Transforms.setNodes(
      editor,
      { type: 'list-item' },
      { match: (n) => SlateElement.isElement(n) && n.type === 'paragraph' }
    );
  }
};



const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)
  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}
const isBlockActive = (editor, format, blockType = 'type') => {
  // Always return false for 'hr' format
  if (format === 'hr') return false;

  const { selection } = editor
  if (!selection) return false
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  )
  return !!match
}
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}
const Element = props => {

  const { attributes, children, element, entryDetails = {}, locale, id } = props
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()
  const style = { textAlign: element.align }
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      const listStyle = element.listStyle || 'disc';
      return (
        <ul
          className='ul-RichText'
          style={{
            ...style,
            listStyle: listStyle
          }}
          {...attributes}
        >
          {children}
        </ul>
      )
    case 'heading-one':
      return (
        <h1 className='h1-RichText' style={style} {...attributes}>
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 className='h2-RichText' style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'heading-three':
      return (
        <h2 className='h3-RichText' style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'heading-four':
      return (
        <h2 className='h4-RichText' style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'list-item':
      return (
        <li className='li-RichText ' style={style} {...attributes}>
          {children}
        </li>
      )
    case 'hr':
      return (
        <div {...attributes}>
          <hr />
          {children}
        </div>
      );
    case 'numbered-list':
      return (
        <ol
          className='ol-RichText'
          style={style}
          {...attributes}
          start={element.start || 1} // Use the start attribute if provided
        >
          {children}
        </ol>
      )
    case 'image':
      return <Image {...props} />

    case 'entry':
      return <EmbedEntry {...props} entryDetails={entryDetails[element.entryId]} />
    case 'link':
      return (
        <a
          {...attributes}
          href={element.url}
          className="a-RichText"
          onClick={(e) => {
            e.stopPropagation();
            // Only open in new tab if Ctrl (or Cmd on Mac) is pressed
            if (e.ctrlKey || e.metaKey) {
              window.open(element.url, '_blank');
            }
          }}
        >
          {children}
        </a>
      );

    case 'table':
      return (
        <TableRender attributes={attributes} element={element} children={children} locale={locale} editorId={id} />
      )
    case 'table-row':
      return <tr {...attributes}>{children}</tr>
    case 'table-cell':
      return (
        <td
          {...attributes}
          style={{
            textAlign: element.align || 'left',
            border: element.borderSize === 0 ? 'none' : `${element.borderSize}px solid gray`,
            padding: '5px',
          }}
        >
          {children}</td>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      )
  }
}
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }
  if (leaf.code) {
    children = <code>{children}</code>
  }
  if (leaf.italic) {
    children = <em>{children}</em>
  }
  if (leaf.underline) {
    children = <u>{children}</u>
  }
  if (leaf.highlight) {
    children = <span style={{ backgroundColor: '#ffff00' }}>{children}</span>
  }
  if (leaf.color) {
    children = <span style={{ color: leaf.color }}>{children}</span>;
  }
  if (leaf.strikethrough) {
    children = <s>{children}</s>;
  }

  return <span {...attributes}>{children}</span>
}
const BlockButton = ({ format, icon, tooltip }) => {
  const editor = useSlate()
  return (
    <Tooltip title={tooltip} arrow>
      <Button
        active={isBlockActive(
          editor,
          format,
          TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
        )}
        onMouseDown={event => {
          event.preventDefault()
          toggleBlock(editor, format)
        }}
      >
        <Icon>{icon}</Icon>
      </Button>
    </Tooltip>
  )
}

const BulletListButton = ({ tooltip }) => {
  const editor = useSlate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const bulletTypes = [
    { type: 'disc', label: '• Disc' },
    { type: 'circle', label: '○ Circle' },
    { type: 'square', label: '■ Square' },
    { type: 'none', label: 'None' }
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBulletTypeChange = (type) => {
    handleClose();

    // First activate bulleted list if not active
    if (!isBlockActive(editor, 'bulleted-list')) {
      toggleBlock(editor, 'bulleted-list');
    }

    Transforms.setNodes(
      editor,
      {
        listStyle: type === 'none' ? 'none' : type,
        className: type === 'none' ? 'no-bullet-RichText' : undefined
      },
      {
        match: n => SlateElement.isElement(n) && n.type === 'bulleted-list'
      }
    );
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Tooltip title="Bulleted List" arrow>
        <Button
          active={isBlockActive(editor, 'bulleted-list')}
          onMouseDown={(event) => {
            event.preventDefault();
            toggleBlock(editor, 'bulleted-list');
          }}
        >
          <Icon>format_list_bulleted</Icon>
        </Button>
      </Tooltip >
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          p: 0,
          height: '14px',
          width: '10px',
          border: '1px solid #e0e0e0',
          borderRadius: '2px',
          ml: '-1px',
          mb: '6px',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#bdbdbd'
          }
        }}
      >
        {open ? (
          <ArrowDropUpIcon fontSize="small" sx={{ fontSize: '16px' }} />
        ) : (
          <ArrowDropDownIcon fontSize="small" sx={{ fontSize: '16px' }} />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {bulletTypes.map((bullet) => (
          <MenuItem
            sx={{ fontSize: '15px', py: 0.5 }}
            key={bullet.type}
            onClick={() => handleBulletTypeChange(bullet.type)}
          >
            {bullet.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const MarkButton = ({ format, icon, tooltip }) => {
  const editor = useSlate()
  return (
    <Tooltip title={tooltip} arrow>
      <Button
        active={isMarkActive(editor, format)}
        onMouseDown={event => {
          event.preventDefault()
          toggleMark(editor, format)
        }}
      >
        <Icon
          className={format === "strikethrough" ? "scale-[1.15] translate-y-[2px]" : ""}
        >{icon}</Icon>
      </Button>
    </Tooltip>
  )
}

const withLinks = (editor) => {
  const { isInline } = editor;

  editor.isInline = (element) => {
    return element.type === 'link' ? true : isInline(element);
  };

  return editor;
};

const withImages = editor => {
  const { insertData, isVoid } = editor
  editor.isVoid = element => {
    return element.type === 'image' ? true : isVoid(element)
  }
  editor.insertData = data => {
    const text = data.getData('text/plain')
    const { files } = data
    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader()
        const [mime] = file.type.split('/')
        if (mime === 'image') {
          reader.addEventListener('load', () => {
            const url = reader.result
            insertImage(editor, url)
          })
          reader.readAsDataURL(file)
        }
      }
    } else if (isImageUrl(text)) {
      insertImage(editor, text)
    } else {
      insertData(data)
    }
  }
  return editor
}
const insertImage = (editor, url) => {
  const text = { text: '' }
  const image = { type: 'image', url, children: [text] }
  Transforms.insertNodes(editor, image)
  Transforms.insertNodes(editor, {
    type: 'paragraph',
    children: [{ text: '' }],
  })
}

const handleEditImage = (editor, path, element) => {
  const alt = prompt("Enter alt text:", element.alt || "");
  console.log(alt);
  console.log(path);
  if (alt !== null) {
    Transforms.setNodes(
      editor,
      { alt },
      { at: path }
    );
  }
};


const Image = ({ attributes, children, element }) => {
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()
  return (
    <div {...attributes}>
      {children}
      <div
        contentEditable={false}
        className={css`
          position: relative;
        `}
      >
        <img
          src={element.url}
          className={css`
            display: block;
            max-width: 100%;
            max-height: 20em;
            box-shadow: ${selected && focused ? '0 0 0 3px #B4D5FF' : 'none'};
          `}
        />
        <Button
          active
          onClick={() => Transforms.removeNodes(editor, { at: path })}
          className={css`
            display: ${selected && focused ? 'inline' : 'none'};
            position: absolute;
            top: 0.5em;
            left: 0.5em;
            background-color: white;
          `}
        >
          <Icon>delete</Icon>
        </Button>

        <Button
          active
          onClick={() => handleEditImage(editor, path, element)}
          className={css`
            display: ${selected && focused ? 'inline' : 'none'};
            position: absolute;
            top: 0.5em;
            right: 0.5em;
            background-color: white;
          `}
        >
          <Icon>edit</Icon>
        </Button>
      </div>
    </div>
  )
}

// Get all entry ID, for now used for getting embed entry publish status
const getAllEntryIdsFromEditor = (editor) => {
  const entryNodes = Editor.nodes(editor, {
    at: [],
    match: n => SlateElement.isElement(n) && n.type === 'entry'
  });

  const entryIds = [];
  for (const [node] of entryNodes) {
    if (node.entryId) {
      entryIds.push(node.entryId);
    }
  }

  return [...new Set(entryIds)]; // Return unique IDs
};



const EmbedEntry = ({ attributes, children, element, entryDetails = {} }) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const selected = useSelected();
  const focused = useFocused();

  const [showMissingEntryMsg, setShowMissingEntryMsg] = useState(false);

  const hasIdentifier = !!entryDetails.identifier;

  useEffect(() => {
    let timeout;

    if (!hasIdentifier) {
      timeout = setTimeout(() => {
        setShowMissingEntryMsg(true);
      }, 3000);
    } else {
      // Clear the error if data arrives later after 3 seconds
      setShowMissingEntryMsg(false);
    }

    return () => clearTimeout(timeout);
  }, [hasIdentifier]);


  return (
    <p {...attributes} contentEditable={false}>
      <div style={{ position: 'relative' }}>
        <Grid2
          container
          sx={{
            border: '1.5px solid #ECECEC',
            borderRadius: 1,
            padding: '8px',
            margin: '0px 8px',
            alignItems: 'center',
          }}
        >
          <Grid2 size={9}>
            {hasIdentifier ? (
              <>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontSize: '18px' }}
                >
                  {entryDetails.identifier}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {element.content_type_name}
                </Typography>
              </>
            ) : showMissingEntryMsg ? (
              <Typography
                sx={{
                  color: '#721c24',
                  borderRadius: '4px',
                }}
              >
                Entry not found or may have been deleted.
              </Typography>
            ) : (
              <CircularProgress size={24} />
            )}
          </Grid2>

          <Grid2
            size={3}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              textAlign: 'right',
            }}
          >
            {hasIdentifier && (
              <Typography
                sx={{
                  color: entryDetails.published ? 'green' : '#856404',
                  backgroundColor: entryDetails.published ? '#e6ffe6' : '#fff3cd',
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: 13,
                  marginRight: '8px',
                }}
              >
                {entryDetails.published ? 'Published' : 'Draft'}
              </Typography>
            )}
            <IconButton
              onClick={() => {
                const path = ReactEditor.findPath(editor, element);
                Transforms.removeNodes(editor, { at: path });
              }}
            >
              <img
                src={DeleteIcon}
                alt="delete icon"
                style={{ width: '20px', height: '20px' }}
              />
            </IconButton>
          </Grid2>
        </Grid2>
        {children}
      </div>
    </p>
  );
};

//Can use for URL upload
//Images
const InsertURLImageButton = () => {
  const editor = useSlateStatic()
  return (
    <Button
      onMouseDown={event => {
        event.preventDefault()
        const url = window.prompt('Enter the URL of the image:')
        if (url && !isImageUrl(url)) {
          alert('URL is not an image')
          return
        }
        url && insertImage(editor, url)
      }}
    >
      <Icon>add_photo_alternate</Icon>
    </Button>
  )
}

// const LinkButton = () => {
//   const editor = useSlateStatic();
//   const [url, setUrl] = useState('');

//   // Function to validate a URL
//   const isValidUrl = (url) => {
//     try {
//       new URL(url); // Throws an error if the URL is invalid
//       return true;
//     } catch (error) {
//       return false;
//     }
//   };

//   const handleClick = () => {
//     const selectedText = window.getSelection().toString();
//     const newUrl = prompt('Enter the URL:', url);

//     if (newUrl) {
//       if (isValidUrl(newUrl)) {
//         setUrl(newUrl);
//         insertLink(editor, newUrl, selectedText);
//       } else {
//         alert('Please enter a valid URL (e.g., https://example.com).');
//       }
//     }
//   };

//   return (

//     <Button onMouseDown={handleClick}>
//       <Tooltip title="Insert Link" arrow>
//         <Icon>link</Icon>
//       </Tooltip>
//     </Button>
//   );
// };

// const insertLink = (editor, url, text) => {
//   const { selection } = editor;

//   if (!selection) {
//     // Insert link with URL as text if no selection
//     Transforms.insertNodes(editor, {
//       type: 'link',
//       url,
//       children: [{ text: text || url }],
//     });

//     // Move cursor outside the link
//     Transforms.move(editor, { distance: 1, unit: 'offset' });

//     // Insert space only if there is no following text
//     const [node] = Editor.node(editor, editor.selection.focus.path);
//     if (!node.text) {
//       Transforms.insertText(editor, ' ');
//     }

//     return;
//   }

//   const isCollapsed = Range.isCollapsed(selection);

//   if (isCollapsed) {
//     // Insert link with URL as text if selection is collapsed
//     Transforms.insertNodes(editor, {
//       type: 'link',
//       url,
//       children: [{ text: text || url }],
//     });

//     Transforms.move(editor, { distance: 1, unit: 'offset' });

//     const [node] = Editor.node(editor, editor.selection.focus.path);
//     if (!node.text) {
//       Transforms.insertText(editor, ' ');
//     }
//   } else {
//     Transforms.wrapNodes(
//       editor,
//       {
//         type: 'link',
//         url,
//         children: [],
//       },
//       { split: true, at: selection }
//     );

//     // Move cursor outside the link
//     Transforms.collapse(editor, { edge: 'end' });
//     Transforms.move(editor, { distance: 1, unit: 'offset' });

//     // Insert space only if there is no following text
//     const [node] = Editor.node(editor, editor.selection.focus.path);
//     if (!node.text) {
//       Transforms.insertText(editor, ' ');
//     }
//   }
// };


const TableRender = ({ attributes, element, children, locale, editorId }) => {

  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()

  const handleEdit = () => {
    const [maybeTable] = Editor.nodes(editor, {
      match: n => n.type === 'table',
      at: path,
    })
    if (maybeTable) {

      const tableButton = document.querySelector(`.table-button-${locale}[data-table-button="${editorId}"]`)

      if (tableButton) {
        const customEvent = new CustomEvent('edit-table', {
          bubbles: true,
          detail: {
            tableData: maybeTable,
            locale,
            editorId
          }
        })
        tableButton.dispatchEvent(customEvent)
      }
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button
        style={{
          display: selected && focused ? 'flex' : 'none',
          position: 'absolute',
          top: '-28px',
          right: 0,
          padding: '4px',
          minWidth: 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px',
          zIndex: 1,
        }}
        onClick={handleEdit}
      >
        <Icon>edit</Icon>
      </Button>
      <table
        {...attributes}
        style={{
          width: element.width || '100%',
          height: element.height || 'auto',
          border: element.borderSize === 0 ? 'none' : `${element.borderSize || 1}px solid gray`,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}



const InsertTableButton = ({ locale, editorId }) => {
  const editor = useSlateStatic();
  const [showModal, setShowModal] = useState(false);
  const [columns, setColumns] = useState(2);
  const [rows, setRows] = useState(2);
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('auto');
  const [borderSize, setBorderSize] = useState(1);
  const [tableClass, setTableClass] = useState('');
  const [editingTable, setEditingTable] = useState(false);
  const [columnError, setColumnError] = useState('');
  const [rowError, setRowError] = useState('');

  useEffect(() => {
    const button = document.querySelector(`.table-button-${locale}[data-table-button="${editorId}"]`)

    const handleEditTable = (e) => {
      const tableData = e.detail?.tableData
      const eventLocale = e.detail?.locale
      const eventEditorId = e.detail?.editorId;

      if (tableData && eventLocale === locale && eventEditorId === editorId) {
        setEditingTable(tableData)
        const node = tableData[0]
        setColumns(node.columns || 2)
        setRows(node.children ? node.children.length : 2)
        setWidth(node.width || '100%')
        setHeight(node.height || 'auto')
        setBorderSize(node.borderSize || 1)
        setTableClass(node.tableClass || '')
        setShowModal(true)
      }
    }

    if (button) {
      button.addEventListener('edit-table', handleEditTable)
      return () => button.removeEventListener('edit-table', handleEditTable)
    }
  }, [locale, editorId])



  const insertTable = (editor, columns, rows, width, height, borderSize, tableClass) => {
    const table = {
      type: 'table',
      width,
      height,
      borderSize,
      columns,
      tableClass,
      children: [],
    };
    for (let r = 0; r < rows; r++) {
      const tableRow = {
        type: 'table-row',
        children: [],
      };
      for (let c = 0; c < columns; c++) {
        tableRow.children.push({
          type: 'table-cell',
          borderSize,
          children: [{ text: '' }],
        });
      }
      table.children.push(tableRow);
    }
    Transforms.insertNodes(editor, [
      table,
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ]);
  };

  const editSelectedTable = () => {
    if (!editingTable) return;

    const [tableNode, path] = editingTable;
    // Check if path and tableNode are valid
    if (!path || !tableNode) {
      console.error('Invalid table node or path');
      return;
    }

    const existingRows = tableNode.children || [];
    const updatedChildren = [];

    for (let r = 0; r < rows; r++) {
      if (r < existingRows.length) {
        const existingCells = existingRows[r].children || [];
        const newCells = [];

        for (let c = 0; c < columns; c++) {
          if (c < existingCells.length) {
            newCells.push({
              ...existingCells[c],
              borderSize
            });
          } else {
            newCells.push({
              type: 'table-cell',
              borderSize,
              children: [{ text: '' }]
            });
          }
        }

        updatedChildren.push({
          type: 'table-row',
          children: newCells.slice(0, columns)
        });
      } else {
        const newRow = {
          type: 'table-row',
          children: Array(columns).fill(null).map(() => ({
            type: 'table-cell',
            borderSize,
            children: [{ text: '' }]
          }))
        };
        updatedChildren.push(newRow);
      }
    }

    Transforms.removeNodes(editor, {
      at: path,
      match: (n) => n.type === 'table',
    });

    Transforms.insertNodes(
      editor,
      {
        type: 'table',
        width,
        height,
        borderSize,
        columns,
        tableClass,
        children: updatedChildren
      },
      { at: path }
    );

    setShowModal(false);
  };

  const isValid = () => {
    return columns >= 1 && rows >= 1;
  };

  const handleCreateOrEdit = () => {
    if (!isValid()) return;

    if (editingTable) {
      editSelectedTable();
    } else {
      insertTable(editor, columns, rows, width, height, borderSize, tableClass);
      setShowModal(false);
    }
  };

  const handleColumnChange = (e) => {
    const value = parseInt(e.target.value);
    setColumns(value);
    setColumnError(value < 1 ? 'Minimum value is 1' : '');
  };

  const handleRowChange = (e) => {
    const value = parseInt(e.target.value);
    setRows(value);
    setRowError(value < 1 ? 'Minimum value is 1' : '');
  };

  const resetToDefaults = () => {
    setColumns(2);
    setRows(2);
    setWidth('100%');
    setHeight('auto');
    setBorderSize(1);
    setColumnError('');
    setTableClass('')
    setRowError('');
    setEditingTable(null);
  };

  const handleCloseModal = () => {
    resetToDefaults();
    setShowModal(false);
  };


  return (
    <div>
      <Tooltip title="Insert Table" arrow>
        <Button
          className={`table-button-${locale}`}
          data-table-button={editorId}
          onMouseDown={(e) => {
            e.preventDefault()
            if (e.type === 'mousedown' && !e.detail?.tableData) {
              resetToDefaults()
              setShowModal(true)
            }
          }}
        >
          < BackupTableIcon sx={{ fontSize: 18, mb: 1 }} />
        </Button>
      </Tooltip>
      <Modal open={showModal} onClose={handleCloseModal}>
        <ModalBox sx={{ width: '520px', maxWidth: '95%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingTable ? 'Edit Table' : 'Create Table'}
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <TextFieldInput
                label='Columns'
                type='number'
                value={columns}
                onChange={handleColumnChange}
                error={!!columnError}
              />
              {columnError && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {columnError}
                </Typography>
              )}
            </Grid2>
            <Grid2 size={6}>
              <TextFieldInput
                label='Rows'
                type='number'
                value={rows}
                onChange={handleRowChange}
                error={!!rowError}
              />
              {rowError && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {rowError}
                </Typography>
              )}
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                type='text'
                label='Width'
                value={width}
                onChange={e => setWidth(e.target.value)}
              />
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                type='text'
                label='Height'
                value={height}
                onChange={e => setHeight(e.target.value)}
              />
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                label='Border Size'
                type='number'
                value={borderSize}
                onChange={e => setBorderSize(parseInt(e.target.value))}
              />
            </Grid2>
            <Grid2 size={12} mb={1}>
              <TextFieldInput
                sx={{ width: '100%' }}
                label='Class name'
                type='text'
                value={tableClass}
                onChange={e => setTableClass(e.target.value)}
              />
            </Grid2>
          </Grid2>
          <Box justifyContent='right' display='flex' margin="10px 0px" gap={1}>
            <OutlinedAddButton
              startIcon={null}
              label='Cancel'
              variant='outlined'
              onClick={handleCloseModal}
            />
            <AddButton
              startIcon={null}
              label={editingTable ? 'Save' : 'Create'}
              onClick={handleCreateOrEdit}
              disabled={!isValid()}
            />
          </Box>
        </ModalBox>
      </Modal>
    </div>
  );
};
// const withTables = (editor) => {
//   const { isVoid, isInline } = editor;

//   editor.isVoid = (element) =>
//       element.type === 'table' || element.type === 'table-row' || element.type === 'table-cell'
//           ? false
//           : isVoid(element);

//   editor.isInline = (element) => (element.type === 'table-cell' ? true : isInline(element));

//   return editor;
// };


const withTables = editor => {
  const { deleteBackward, deleteForward, insertBreak } = editor
  editor.deleteBackward = unit => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })
      if (cell) {
        const [, cellPath] = cell
        const start = Editor.start(editor, cellPath)
        if (Point.equals(selection.anchor, start)) {
          return
        }
      }
    }
    deleteBackward(unit)
  }
  editor.deleteForward = unit => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })
      if (cell) {
        const [, cellPath] = cell
        const end = Editor.end(editor, cellPath)
        if (Point.equals(selection.anchor, end)) {
          return
        }
      }
    }
    deleteForward(unit)
  }
  editor.insertBreak = () => {
    const { selection } = editor
    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table',
      })
      if (table) {
        return
      }
    }
    insertBreak()
  }
  return editor
}


//Can use for image upload to rich Text editor
const InsertImageButton = () => {
  const editor = useSlateStatic()

  const handleImageUpload = async (file) => {

    const token = localStorage.getItem('token');
    const workspaceID = localStorage.getItem('workspaceID');


    const formData = new FormData()
    formData.append('file', file)
    formData.append('workspaceId', workspaceID)

    const headers = new Headers();
    headers.append('Authorization', `Bearer ${token}`);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/dashboard/rich-text/upload`, {
        method: 'POST',
        body: formData,
        headers: headers,
      })
      const data = await response.json()
      if (data && data.url) {
        insertImage(editor, data.url)
      } else {
        alert('Failed to upload image')
      }
    } catch (error) {
      console.error('Image upload failed:', error)
      alert('Image upload failed')
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file)
    } else {
      alert('Please select a valid image file')
    }
  }

  return (
    <Button>
      <label>
        <Icon style={{ cursor: 'pointer' }}>image</Icon>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </label>
    </Button>
  )
}

const EmbedButton = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [addExistingEntryPopup, setAddExistingEntryPopup] = useState(false);
  const [addExistingMediaPopup, setAddExistingMediaPopup] = useState(false);
  const [addNewMediaPopup, setAddNewMediaPopup] = useState(false);
  const workspaceId = localStorage.getItem('workspaceID')
  const editor = useSlateStatic();
  const entries = useSelector((state) => state.entry.entries) || [];
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const anchorRef = useRef(null);

  const handleRichTextAddEntry = (selectedEntriesIds) => {
    setShowMenu(false);
    setAddExistingEntryPopup(false);
    const selectedIds = selectedEntriesIds.map(item => item._id);

    const selectedEntries = entries.filter(entry =>
      selectedIds.includes(entry._id)
    );

    selectedEntries.forEach((entry, index) => {
      const entryNode = {
        type: 'entry',
        entryId: entry._id,
        identifier: entry.identifier,
        content_type_name: entry.content_type_name,
        published: entry.published,
        children: [{ text: '' }]
      }

      Transforms.insertNodes(editor, entryNode);

      if (index === selectedEntries.length - 1) {
        Transforms.insertNodes(editor, {
          type: 'paragraph',
          children: [{ text: '' }]
        })
      }
    });
  }

  const handleAddExistingMedia = (selectedAssets) => {
    setShowMenu(false);
    setAddExistingMediaPopup(false);

    selectedAssets.forEach((asset, index) => {
      // Create media node for Slate editor
      const mediaNode = {
        type: 'image',
        url: asset.url,
        alt: asset.title || 'Media',
        children: [{ text: '' }]
      };

      // Insert the media node
      Transforms.insertNodes(editor, mediaNode);

      // Add a paragraph after every image (including the last one)
      Transforms.insertNodes(editor, {
        type: 'paragraph',
        children: [{ text: '' }]
      });

      if (index < selectedAssets.length - 1) {
        Transforms.move(editor, { distance: 1, unit: 'line' });
      }
    });
  };

  const handleAddNewMedia = async (response) => {
    try {
      setShowMenu(false);
      setAddNewMediaPopup(false);

      // Get asset from the return of addAsset response
      const newAsset = response.asset;

      if (newAsset) {
        // Create media node for Slate editor
        const mediaNode = {
          type: 'image',
          url: newAsset.url,
          alt: newAsset.title || 'Media',
          assetId: newAsset._id,
          children: [{ text: '' }]
        };

        // Insert the media node
        Transforms.insertNodes(editor, mediaNode);

        Transforms.insertNodes(editor, {
          type: 'paragraph',
          children: [{ text: '' }]
        });
      }
    } catch (error) {
      console.error('Error adding new media:', error);
    }
  };


  return (
    <>
      <div style={{ position: "relative", display: "inline-block" }}>
        <Tooltip title="Embed Content" arrow>
          <Button
            ref={anchorRef}
            onMouseDown={(e) => {
              e.preventDefault();
              setShowMenu((prev) => !prev);
            }}
            className="border border-gray-500 rounded-md text-center justify-center flex gap-2"
          >
            <Icon className="text-gray-500">add</Icon>
            <span className="text-gray-500 text-sm">Embed</span>
            <Icon
              className={`text-gray-500 transition-transform pt-0.5 ${showMenu ? "rotate-0" : "rotate-180"
                }`}
            >
              arrow_drop_down
            </Icon>
          </Button>
        </Tooltip>

        <Menu
          anchorEl={anchorRef.current}
          open={showMenu}
          onClose={() => setShowMenu(false)}
          autoFocus={false}
          PaperProps={{
            className:
              "z-50 w-48 mt-1 bg-white border text-gray-700 border-gray-300 rounded-md shadow-md",
          }}
          sx={{
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              color: '#616161'
            }
          }}
        >
          <MenuItem
            onClick={() => {
              setAddExistingEntryPopup(true);
              setShowMenu(false);
            }}
            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
          >
            Entry
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAddExistingMediaPopup(true);
              setShowMenu(false);
            }}
            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
          >
            Add Existing Media
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAddNewMediaPopup(true);
              setShowMenu(false);
            }}
            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
          >
            Add New Media
          </MenuItem>
        </Menu>
      </div>

      <AddExistingEntryPopup
        open={addExistingEntryPopup}
        onClose={() => setAddExistingEntryPopup(false)}
        onConfirm={handleRichTextAddEntry}
      // initialExistingEntries={initialExistingEntries}
      />

      <ExistingMediaUploadPopup
        open={addExistingMediaPopup}
        onClose={() => setAddExistingMediaPopup(false)}
        onConfirm={handleAddExistingMedia}
        workspaceId={workspaceId}
      />

      <AddMedia
        isOpen={addNewMediaPopup}
        onClose={() => setAddNewMediaPopup(false)}
        onSubmitSuccess={handleAddNewMedia}
        editMode={false}
      />
    </>
  );
};


const isImageUrl = url => {
  if (!url) return false
  if (!isUrl(url)) return false
  const ext = new URL(url).pathname.split('.').pop()
  return imageExtensions.includes(ext)
}

RichText.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  locale: PropTypes.any
};

export default RichText